import { Alert, Radio, Space, Typography } from 'antd'
import React, { ReactElement, useEffect, useState } from 'react'
import { useStore } from 'react-redux'

import { CardinalApiState, generateRecoveryKeyForCurrentUser, getApiFromState, RecoveryKeyScope } from '../../../core/services/auth.api'
import { CustomModal } from '../../common/CustomModal'
import { SpinLoader } from '../../common/SpinLoader'

import './index.less'

interface ModalAccessRecoveryKeyProps {
  isVisible: boolean
  onClose: () => void
}

export const ModalAccessRecoveryKey = ({ isVisible, onClose }: ModalAccessRecoveryKeyProps): ReactElement => {
  const store = useStore<{ cardinalApi: CardinalApiState }>()
  const [scope, setScope] = useState<RecoveryKeyScope>('self-and-parents')
  const [parentId, setParentId] = useState<string | undefined>(undefined)
  const [hierarchyLoaded, setHierarchyLoaded] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [recoveryKey, setRecoveryKey] = useState<string | undefined>(undefined)
  const [error, setError] = useState<string | undefined>(undefined)
  const [hasAutoGenerated, setHasAutoGenerated] = useState(false)

  const hasParent = !!parentId
  const showScopePicker = hierarchyLoaded && hasParent

  // Resolve the immediate parent (if any) so the parent-only option can be wired
  // to the right SDK call. Hierarchy is ordered top-most ancestor → current data
  // owner; the immediate parent sits at length-2.
  useEffect(() => {
    if (!isVisible) return
    let cancelled = false
    void (async () => {
      try {
        const sdk = await getApiFromState(() => store.getState().cardinalApi)
        if (cancelled || !sdk) return
        const ids = await sdk.dataOwner.getCurrentDataOwnerHierarchyIds()
        if (cancelled) return
        setParentId(ids.length >= 2 ? ids[ids.length - 2] : undefined)
      } catch {
        if (!cancelled) setParentId(undefined)
      } finally {
        if (!cancelled) setHierarchyLoaded(true)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [isVisible, store])

  const reset = () => {
    setScope('self-and-parents')
    setRecoveryKey(undefined)
    setError(undefined)
    setGenerating(false)
    setHasAutoGenerated(false)
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const generateWithScope = async (s: RecoveryKeyScope) => {
    setError(undefined)
    setGenerating(true)
    try {
      const sdk = await getApiFromState(() => store.getState().cardinalApi)
      if (!sdk) {
        setError('You must be logged in to generate a recovery key.')
        return
      }
      const key = await generateRecoveryKeyForCurrentUser(sdk, s)
      setRecoveryKey(key)
    } catch (e) {
      setError(`Could not generate the recovery key: ${(e as Error).message}`)
    } finally {
      setGenerating(false)
    }
  }

  // No parent → no choice to make, generate the key immediately so the user lands
  // on the key reveal directly. Single-fire via `hasAutoGenerated`; on error the
  // user can retry with the primary button.
  useEffect(() => {
    if (!isVisible || !hierarchyLoaded || hasParent || hasAutoGenerated || recoveryKey || generating) return
    setHasAutoGenerated(true)
    void generateWithScope('self-and-parents')
  }, [isVisible, hierarchyLoaded, hasParent, hasAutoGenerated, recoveryKey, generating])

  const handleGenerate = () => generateWithScope(scope)

  const primaryBtnTitle = recoveryKey ? 'Done' : generating ? 'Generating…' : 'Generate key'
  const handlePrimary = recoveryKey ? handleClose : handleGenerate
  const primaryDisabled = !recoveryKey && (generating || !hierarchyLoaded)

  return (
    <CustomModal
      isVisible={isVisible}
      handleClose={handleClose}
      secondaryBtnTitle={recoveryKey ? undefined : 'Cancel'}
      handleClickPrimaryBtn={handlePrimary}
      primaryBtnTitle={primaryBtnTitle}
      primaryBtnDisabled={primaryDisabled}
      title="Access recovery key"
    >
      <div className="modalAccessRecoveryKey">
        {generating && <SpinLoader />}
        {recoveryKey ? (
          <>
            <Alert
              type="error"
              showIcon
              title="Save this key in a safe place"
              description="It grants access to the corresponding private keys. We do not store it on our servers — it cannot be shown again once this dialog is closed."
            />
            <Alert
              type="info"
              title="Recovery key"
              description={
                <Typography.Paragraph className="modalAccessRecoveryKey__keyValue" copyable={{ text: recoveryKey }}>
                  {recoveryKey}
                </Typography.Paragraph>
              }
            />
          </>
        ) : (
          <>
            <p className="modalAccessRecoveryKey__intro">
              {showScopePicker
                ? 'Generate a recovery key that can be used to recover the corresponding private keys if local storage is lost. Choose the scope of the key.'
                : 'Generating a recovery key for your account. Once shown, store it somewhere safe — it cannot be re-displayed.'}
            </p>

            {error && <Alert type="error" showIcon title={error} />}

            {showScopePicker && (
              <Radio.Group className="modalAccessRecoveryKey__scope" value={scope} onChange={(e) => setScope(e.target.value as RecoveryKeyScope)} disabled={generating}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Radio value="self-and-parents">
                    <span className="modalAccessRecoveryKey__scopeTitle">My keys and the parent account&apos;s keys</span>
                    <span className="modalAccessRecoveryKey__scopeDesc">Recovers all of your private keys plus those of the parent account (and its ancestors).</span>
                  </Radio>
                  <Radio value="parents-only" disabled={generating}>
                    <span className="modalAccessRecoveryKey__scopeTitle">Parent account&apos;s keys only</span>
                    <span className="modalAccessRecoveryKey__scopeDesc">Recovers only the immediate parent account’s private keys.</span>
                  </Radio>
                </Space>
              </Radio.Group>
            )}
          </>
        )}
      </div>
    </CustomModal>
  )
}
